import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, AlertTriangle, ScanEye, Globe, Siren } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import AnalysisInput from '../components/AnalysisInput';
import AnalysisResult from '../components/AnalysisResult';
import ChatPanel from '../components/ChatPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function HomePage() {
  const { token, user, refreshUser } = useAuth();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [availableLlms, setAvailableLlms] = useState({});

  useEffect(() => {
    axios.get(`${API_URL}/api/llms`).then(res => setAvailableLlms(res.data)).catch(() => {});
  }, []);

  const detectPlatform = (url) => {
    if (url.includes('instagram')) return 'instagram';
    if (url.includes('twitter') || url.includes('x.com')) return 'x';
    if (url.includes('tiktok')) return 'tiktok';
    if (url.includes('facebook')) return 'facebook';
    return 'web';
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleAnalyze = async (type, content, selectedLlms, socialScreenshot = null, customInstructions = null) => {
    if (!user) { navigate('/login'); return; }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      let analysisContent = content;
      let contentType = type;
      let imageBase64 = null;

      if (type === 'file' && content instanceof File) {
        const formData = new FormData();
        formData.append('file', content);
        const uploadRes = await axios.post(`${API_URL}/api/upload`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        analysisContent = uploadRes.data.encoded;
        contentType = uploadRes.data.content_type;
      }

      if (type === 'social_post' && socialScreenshot instanceof File) {
        imageBase64 = await fileToBase64(socialScreenshot);
      }

      const payload = {
        content_type: contentType === 'profile' ? 'text' : contentType,
        content: analysisContent,
        platform: type === 'url' ? detectPlatform(content) : (type === 'social_post' ? 'social_media' : null),
        selected_llms: selectedLlms,
        language: language,
      };

      if (customInstructions) {
        payload.custom_instructions = customInstructions;
      }

      if (imageBase64) {
        payload.image_base64 = imageBase64;
      }

      const res = await axios.post(`${API_URL}/api/analyze`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setResult(res.data);
      await refreshUser();
    } catch (err) {
      setError(err.response?.data?.detail || t('home_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleShare = (result) => {
    if (navigator.share) {
      navigator.share({ title: 'Fact-Check Alyse', text: result.shareable_summary });
    }
  };

  return (
    <div className="min-h-screen hero-gradient pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6">
            <Zap className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-400 text-sm font-medium">{t('home_badge')}</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold font-heading mb-6">
            <span className="gradient-text">{t('home_title1')}</span>
            <br />
            <span className="text-white">{t('home_title2')}</span>
          </h1>

          <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto">
            {t('home_subtitle')}
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <AnalysisInput onAnalyze={handleAnalyze} loading={loading} user={user} />
        </motion.div>

        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="max-w-3xl mx-auto mt-6 p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-center gap-3"
            >
              <AlertTriangle className="w-5 h-5 text-rose-400" />
              <p className="text-rose-400">{error}</p>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {result && (
            <>
              <AnalysisResult
                result={result}
                onClose={() => setResult(null)}
                onShare={handleShare}
                onChallenge={(challengeResult) => {
                  if (challengeResult.challenge_accepted) {
                    setResult({
                      ...result,
                      verdict: challengeResult.updated_verdict,
                      confidence_score: challengeResult.updated_confidence,
                      metadata: { ...result.metadata, challenged: true, challenge_accepted: true }
                    });
                  }
                }}
              />
              <ChatPanel analysisId={result.id} availableLlms={availableLlms} />
            </>
          )}
        </AnimatePresence>

        {!result && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="grid md:grid-cols-3 gap-6 mt-20"
          >
            {[
              { icon: ScanEye, title: t('home_feat_deepfake'), desc: t('home_feat_deepfake_desc') },
              { icon: Globe, title: t('home_feat_multi'), desc: t('home_feat_multi_desc') },
              { icon: Siren, title: t('home_feat_scam'), desc: t('home_feat_scam_desc') }
            ].map((feature, idx) => (
              <div key={idx} className="glass rounded-2xl p-6 card-hover">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-blue-500/20 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{feature.title}</h3>
                <p className="text-slate-400 text-sm">{feature.desc}</p>
              </div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}
